import {GlobalEventsMap} from '@gongxigames/framework/slot';
import {ErrorBodyData, ErrorCodeType} from '@gongxigames/framework/integrations/rubyplay/RubyPlayErrorsHandler';
import {ResponseBody} from '@gongxigames/framework/integrations/rubyplay/types';

export const OptionsNames = ['MUTE', 'TURBO', 'ABOUT', 'PAYTABLE'] as const;

export type Options = typeof OptionsNames[number];

export type Game2GCMClientSideMessages = {
    'com.lnw.gcm.initialiseChannel': void;
    'com.lnw.gcm.setGameConfig': {
        cuiPosition: string;
        gameLoadingScreen: boolean;
        gameName: string;
    };
    'com.lnw.gcm.gameReady': {
        settings?: LAWRegulationSettings;
    };
    'com.lnw.gcm.loadProgressUpdate': {
        percent: number;
    };
    'com.lnw.gcm.regOption': {
        optionType: string;
    };
    'com.lnw.gcm.balancesUpdate': {
        balances: {
            CASH: number | string;
            BONUS: number | string;
        };
        fudge?: number;
    };
    'com.lnw.gcm.paidUpdate': {
        amount: number;
    };
    'com.lnw.gcm.stakeUpdate': {
        amount: number;
    };
    'com.lnw.gcm.optionHasChanged': {
        optionType: Options;
        changedFrom: 'GAME';
        newValue: boolean;
    };
    'com.lnw.gcm.handleError': {
        category: string;
        severity: string;
        errorCode: string;
        message: string;
    };
    'com.lnw.gcm.handleMessageTrigger': {
        msgTriggerData: string;
    };
    'com.lnw.gcm.formatAmount': {
        amount: number;
    };
    'com.lnw.gcm.resume': ResumeResponse;
    'com.lnw.gcm.gameAnimationComplete': void;
    'com.lnw.gcm.gameAnimationStart': void;
    'com.lnw.gcm.setPromoInfo': {};
    'com.lnw.gcm.getPromoInfo': GetPromoInfoResponse;
    'com.lnw.gcm.getOgsClientTokenResponse': {
        token: string;
    };
    'com.lnw.gcm.reload': void;
    'com.lnw.gcm.redirect': {
        url: string;
    };
};

export type ResumeResponse = {
    triggerEvent: string;
};

export type GetPromoInfoResponse = {
    promoInfo: Record<string, any>;
};

export type PaidUpdateResponse<T extends {value: number;}> = {
    formattedValue: T;
};

export type BalancesHasChangedResponse = {
    balances: Record<string, any>;
};

export type BalancesUpdateResponse = {
    formattedValue: {
        BONUS: {
            value: number;
        };
        CASH: {
            value: number;
        };
    };
};

export type FormatAmountResponse = {
    formattedAmount: string;
};

export type OptionHasChangedResponse = {
    option: Options;
    value: boolean;
};

export type HandleFreeRoundsUpdateResponse = {
    freeRoundsInfo: Record<string, any>;
};

export type LAWRegulationSettings = {
    regulations: string[];
    betSettings: {
        initialBet: number;
        minBet: number;
    };
};

export type OGSParamResponse = Record<string, string>;
export type OGSParamsResponse = [OGSParamResponse];

export type GCM2GameClientSideMessages = {
    'com.lnw.gcm.setOGSParams': OGSParamsResponse;
    'com.lnw.gcm.resume': ResumeResponse;
    'com.lnw.gcm.getPromoInfoResponse': GetPromoInfoResponse;
    'com.lnw.gcm.paidUpdateResponse': PaidUpdateResponse<{value: number;}>;
    'com.lnw.gcm.balancesHasChanged': BalancesHasChangedResponse;
    'com.lnw.gcm.balancesUpdateResponse': BalancesUpdateResponse;
    'com.lnw.gcm.formatAmountResponse': FormatAmountResponse;
    'com.lnw.gcm.getOgsClientToken': void;
    'com.lnw.gcm.optionHasChanged': OptionHasChangedResponse;
    'com.lnw.gcm.stopAutoPlay': void;
    'com.lnw.gcm.handleFreeRoundsUpdate': HandleFreeRoundsUpdateResponse;
    'com.lnw.gcm.gameRevealed': void;
};

export type LightAndWonderMessages = {
    'lnw.clientInsufficientFunds': void;
    'lnw.refreshBalanceRequest': void;
};

export type Game2GCMClientSideMessagesMap = { [key in keyof Game2GCMClientSideMessages]: Game2GCMClientSideMessages[key] };
export type GCM2GameClientSideMessagesMap = { [key in keyof GCM2GameClientSideMessages]: GCM2GameClientSideMessages[key] };

export type LightAndWonderEventsMap = GlobalEventsMap & GCM2GameClientSideMessagesMap & {
    [key: string | symbol]: any;
};

type LAWErrorCategory = 'NON_RECOVERABLE_ERROR' | 'RECOVERABLE_ERROR' | 'CRITICAL' | 'LOGIN_ERROR' | 'INSUFFICIENT_FUNDS' | 'CONNECTION_ERROR';
type LAWErrorCodes = '0' | '1' | '2' | '3' | '102' | '110' | '1000' | '1003' | '1006' | '1007' | '1008' | '1019' | '1035' | '1041' | '1050' | '1109';

export const RubyErrorCodeToLAWCategory: Record<ErrorCodeType, LAWErrorCategory> = {
    'PA-2': 'CRITICAL',
    'PA-3': 'LOGIN_ERROR',
    'PA-4': 'NON_RECOVERABLE_ERROR',
    'PA-6': 'CRITICAL',
    'PA-13': 'CRITICAL',
    'GS-2': 'CRITICAL',
    'GS-3': 'LOGIN_ERROR',
    'GS-4': 'CRITICAL',
    'GS-5': 'INSUFFICIENT_FUNDS',
    'GS-7': 'LOGIN_ERROR',
    'GS-10': 'CRITICAL',
    'GS-15': 'CRITICAL',
    'GS-16': 'CRITICAL',
    'GS-17': 'CRITICAL',
    'GS-18': 'CRITICAL',
    'GS-28': 'CRITICAL',
    'GS-95': 'CRITICAL',
    'GS-96': 'CRITICAL',
    'GS-97': 'CRITICAL',
    'GS-12': 'CRITICAL',
    'GS-13': 'CRITICAL',
    'WALLET-1': 'CRITICAL',
    'GS-98': 'RECOVERABLE_ERROR',
    'GS-99': 'CRITICAL',
    'M-1': 'NON_RECOVERABLE_ERROR',
    'M-4': 'CRITICAL',
    'M-5': 'NON_RECOVERABLE_ERROR'
};

export const RubyErrorCodeLAWErrorCodes: Record<ErrorCodeType, LAWErrorCodes> = {
    'PA-2': '1035',
    'PA-3': '1000',
    'PA-4': '1',
    'PA-6': '1041',
    'PA-13': '1000',
    'GS-2': '1035',
    'GS-3': '1000',
    'GS-4': '1',
    'GS-5': '1006',
    'GS-7': '1000',
    'GS-10': '1',
    'GS-15': '1',
    'GS-16': '1',
    'GS-17': '1',
    'GS-18': '1',
    'GS-28': '1',
    'GS-95': '1',
    'GS-96': '1',
    'GS-97': '1',
    'GS-12': '1',
    'GS-13': '1',
    'WALLET-1': '1',
    'GS-98': '0',
    'GS-99': '1',
    'M-1': '1',
    'M-4': '1',
    'M-5': '1'
};

export type LAWGameServerError = ResponseBody & ErrorBodyData & {
    integrationsPayload: string;
};

export type LAWError = {
    errorCategory: string;
    message: string;
    operatorErrorCode: string;
};